import type { ProjectModel } from '@bryntum/schedulerpro/schedulerpro.module.js';
import { CalendarModel, DateHelper, EventModel } from '@bryntum/schedulerpro/schedulerpro.module.js';
import { isSameDay, isValid, parse, parseISO } from 'date-fns';
import { isEmpty, isNil, uniqBy } from 'lodash';
import type { Production as ProductionServiceProduction } from 'sr-types/lib/production/v1/graphql';
import { Activity, Product, Production, Resource } from 'sr-types/lib/search/v1/graphql';
import { CONSTANTS_ID, FETCH_POLICY_CONFIG, TIME_CONSTANTS } from '../common/constants/constants';
import { searchClient, slimResultsQuery } from '../common/list/slimQuery';
import { PLANNING, PRODUCTION_PROFESSIONAL } from '../common/nav/apps';
import { Reference } from '../common/reference/reference';
import {
    API_TIME_FORMAT,
    DateRange,
    isAtLeast24hoursApart,
    isStartOfDay,
    toBryntumCalTime,
    toDateTimeISOWithoutOffset,
    toISOFromJSDate,
    toSimpleTimeFromJSDate
} from '../common/utils/dateTime';
import { roundUpByDurationUnit } from '../supply/booking/ScheduleCalculator';
import { lineItemGroupTypes } from '../supply/opportunity/common/opportunityConstants';
import { BRYNTUM_CALENDAR_FIELDS, START_OF_DAY, TIME_UNITS } from './schedulerConstants';
import { SchedulerLineItem } from './types';
import {
    DURATION_UNIT_MAPPING,
    SR_TYPES,
    getEventAttributes,
    toSchedulerActivityItemsFromProduction as prepareActivityItems,
    toBryntumEventsAndAssignments as prepareReservationEvents,
    toBryntumEventsAndAssignmentsForActivity as prepareActivityEvents,
    toSchedulerLineItemsFromReservations as prepareReservationItems,
    updateEventsForRenderingOnTimeline as prepareTimelineEvents,
    getMergedStartEnd as mergeTimelineDates
} from './timelineEventFormatting';

// Preserve the existing entry points; capture clock-dependent defaults outside the pure formatters.
export const toSchedulerActivityItemsFromProduction = (activity: Parameters<typeof prepareActivityItems>[0]) => {
    const schedulerActivityItem = prepareActivityItems(activity, new Date());
    return schedulerActivityItem;
};

export const toBryntumEventsAndAssignments = (lineItems: SchedulerLineItem[], hasDependencies = false) => {
    const timelineChanges = prepareReservationEvents(lineItems, hasDependencies, new Date());
    return timelineChanges;
};

export const toBryntumEventsAndAssignmentsForActivity = (lineItems, application) => {
    const timelineChanges = prepareActivityEvents(lineItems, application, new Date());
    return timelineChanges;
};

export const toSchedulerLineItemsFromReservations = (reservations) => {
    const schedulerLineItems = prepareReservationItems(reservations, new Date());
    return schedulerLineItems;
};

export const updateEventsForRenderingOnTimeline = (lineItems: SchedulerLineItem[]) => {
    const timelineChanges = prepareTimelineEvents(lineItems, new Date());
    return timelineChanges;
};

export const getMergedStartEnd = (dateRange: DateRange) => {
    const mergedDates = mergeTimelineDates(dateRange, new Date());
    return mergedDates;
};

export type ActivityEventData = Activity & {
    id: string;
    headline: string;
    about: string | null;
    reference: Reference;
    show: Object;
    activityStatus: string;
    resourceReferences: Reference | null;
    production: Production;
    srType: string;
    type: string;
    manuallyScheduled: boolean;
    duration: number;
    durationUnit: string;
    locationCity: string;
    locationCountry: string;
    cls: string;
    showInTimeline: boolean;
    calendar: string | null;
};

export type SchedulerLineItemFromBryntumEvent = {
    id: string | number;
    dateRange: DateRange;
};

type MoveProductionScheduleActivity = {
    identity?: { id?: string };
    id?: string;
    calendar?: string;
    dateRange?: DateRange;
};

type MoveProductionScheduleActivityTimingMeta = {
    activityId?: string;
    calendar?: string;
    isHourly: boolean;
    durationUnit?: string;
    duration?: number;
    durationSpecified: boolean;
    mergedStart?: Date;
    mergedEnd?: Date;
    startValid: boolean;
    endValid: boolean;
};

type ToSchedulerLineItemOptions = {
    adjustMultiDayEnd?: boolean;
    movingProduction?: boolean;
};

type AssignmentIdentifier = string | number;

type ReservationAssignmentResource =
    | AssignmentIdentifier
    | {
          id?: AssignmentIdentifier | null;
          reference?: {
              id?: AssignmentIdentifier | null;
          } | null;
      }
    | null
    | undefined;

type ReservationAssignmentEvent = {
    id?: AssignmentIdentifier | null;
    lineItemId?: AssignmentIdentifier | null;
    attributes?: {
        lineItemId?: AssignmentIdentifier | null;
    };
    resources?: ReservationAssignmentResource[];
};

type ReservationAssignmentFromEvent = {
    resourceId: AssignmentIdentifier;
    eventId: AssignmentIdentifier;
    lineItemId?: AssignmentIdentifier | null;
};

const getReservationAssignmentResourceId = (resource: ReservationAssignmentResource) => {
    if (typeof resource === 'object') {
        if (hasAssignmentIdentifier(resource?.id)) {
            return resource.id;
        }

        return resource?.reference?.id;
    }

    return resource;
};

export const SYSTEM_CALENDAR = '7 Day Working';

export const getCalendars = (activeOrganizationAccount) => {
    return searchClient.query({
        query: slimResultsQuery(BRYNTUM_CALENDAR_FIELDS.calendar),
        variables: {
            filters: [{ identifier: 'entity', value: BRYNTUM_CALENDAR_FIELDS.calendar }],
            page: {
                from: 0,
                size: 200
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        }
    });
};

export const toBryntumCalendarIntervals = (calendar) => {
    const intervals = [];

    calendar.workingTime.timeIntervals.forEach((currentInterval, index) => {
        if (currentInterval.startTime === currentInterval.endTime) {
            let nextDay = '';

            if (index === calendar.workingTime.timeIntervals.length - 1) {
                const nextDayIndex = daysOfTheWeek.findIndex((day) => day === currentInterval.day);
                nextDay = daysOfTheWeek[nextDayIndex + 1] || daysOfTheWeek[0];
            } else {
                nextDay = calendar.workingTime.timeIntervals[index + 1].day;
            }

            intervals.push({
                recurrentStartDate: `on ${currentInterval.day} at ${toBryntumCalTime(currentInterval.startTime)}`,
                recurrentEndDate: `on ${nextDay} at ${toBryntumCalTime(currentInterval.endTime)}`,
                isWorking: true
            });
        } else {
            intervals.push({
                recurrentStartDate: `on ${currentInterval.day} at ${toBryntumCalTime(currentInterval.startTime)}`,
                recurrentEndDate: `on ${currentInterval.day} at ${toBryntumCalTime(currentInterval.endTime)}`,
                isWorking: true
            });
        }
    });

    return intervals;
};

export const getAttributesFromOpportunity = (opportunity) => {
    let attributes = getEventAttributes(opportunity);
    const orgName =
        opportunity.organization && opportunity.organization.label
            ? opportunity.organization.label
            : opportunity.organization?.organizationReference?.label;
    const prodName =
        opportunity.production && opportunity.production.label
            ? opportunity.production.label
            : opportunity.production?.productionReference?.label;
    attributes.status = opportunity.status;
    attributes.opportunityName = opportunity.name;
    attributes.opportunityType = opportunity.opportunityType;
    attributes.organization = orgName || '';
    attributes.production = prodName || '';
    attributes.opportunityId = opportunity.identity.id;
    attributes.reservationGuestsAvailable = opportunity.guests?.length > 0 && opportunity.primary;
    attributes.ownerOrganizationName = opportunity.ownerOrganizationName;

    if (opportunity.contacts && opportunity.contacts.length) {
        const c = opportunity.contacts[0];
        if (c.role) {
            attributes.contact = `${c.name.firstName} ${c.name.lastName} - ${c.role}`;
        } else {
            attributes.contact = `${c.name.firstName} ${c.name.lastName}`;
        }
    }
    return attributes;
};

export const toSchedulerLineItemFromBooking = (opportunity, lineItem) => {
    let oppAttributes = getAttributesFromOpportunity(opportunity);
    const attributes = { ...oppAttributes };
    const startTime: Date = parse(lineItem.dateRange.startTime, API_TIME_FORMAT, new Date());
    const endTime: Date = parse(lineItem.dateRange.endTime, API_TIME_FORMAT, new Date());
    if (isValid(startTime) && isValid(endTime)) {
        attributes.showTime = true;
    }
    attributes.groupType = lineItem.groupType;
    attributes.parent = lineItem.parent;
    attributes.isChild = !lineItem.needsDetailedSchedule;
    attributes.primary = lineItem.primary;
    attributes.reservationForName = lineItem.product?.label;
    attributes.lineItemId = lineItem.identity.id;
    const assignedResources = lineItem.associatedItems
        ? lineItem.associatedItems.map((r) => {
              return { id: r?.reference?.id, name: r?.label };
          })
        : [];
    const resources = [...lineItem.associatedItems?.map((ai) => ai?.reference?.id), lineItem.product?.reference?.id];
    return {
        id: lineItem.identity.id,
        attributes: attributes,
        dateRange: lineItem.dateRange,
        primary: lineItem.primary,
        assignedResources: assignedResources,
        resources: resources,
        name: lineItem.name,
        status: attributes.status,
        ownerOrganizationName: attributes.ownerOrganizationName,
        permissions: opportunity.permissions,
        calendar: lineItem.calendar && lineItem.calendar.id
    };
};

export const toSchedulerLineItemsFromBookingLineItems = (opportunity) => {
    let oppAttributes = getAttributesFromOpportunity(opportunity);
    return opportunity.lineItems.map((li) => {
        const attributes = { ...oppAttributes };
        const startTime: Date = parse(li.dateRange.startTime, API_TIME_FORMAT, new Date());
        const endTime: Date = parse(li.dateRange.endTime, API_TIME_FORMAT, new Date());
        if (isValid(startTime) && isValid(endTime)) {
            attributes.showTime = true;
        }
        attributes.groupType = li.groupType;
        attributes.parent = li.parent;
        attributes.isChild = !li.needsDetailedSchedule;
        attributes.primary = li.primary;
        attributes.reservationForName = li.product?.label;
        attributes.lineItemId = li.identity.id;
        attributes.shouldSequenceSchedule = li.shouldSequenceSchedule;
        const assignedResources = li.associatedItems
            ? li.associatedItems.map((r) => {
                  return { id: r?.reference?.id, name: r?.label };
              })
            : [];
        const resources = [...li.associatedItems?.map((ai) => ai?.reference?.id), li.product?.reference?.id];
        return {
            id: li.identity.id,
            attributes: attributes,
            dateRange: li.dateRange,
            primary: li.primary,
            assignedResources: assignedResources,
            resources: resources,
            name: li.name,
            status: attributes.status,
            ownerOrganizationName: attributes.ownerOrganizationName,
            permissions: opportunity.permissions,
            calendar: li.calendar && li.calendar.id
        };
    });
};

export const getAdjustedEndDateForMultiDay = (
    start: Date | null | undefined,
    end: Date | null | undefined
): Date | null | undefined => {
    if (!start || !end) return end;
    if (isAtLeast24hoursApart(start, end) && isStartOfDay(end)) {
        return DateHelper.add(end, -1, TIME_CONSTANTS.d);
    }
    return end;
};

/**
 * Converts an array of Bryntum EventModel instances into an array of SchedulerLineItemFromBryntumEvent objects,
 * applying necessary adjustments for multi-day and hourly events, and removes the processed events from the scheduler's event store.
 *
 * @param events - The array of Bryntum EventModel objects to convert.
 * @param project - The Bryntum project containing the event store.
 * @param locale - The locale used for duration rounding and formatting.
 * @param options - Optional configuration for conversion behavior.
 * @param options.adjustMultiDayEnd - If true, adjusts the end date for multi-day events to ensure proper boundaries.
 * @returns An array of SchedulerLineItemFromBryntumEvent objects representing the converted events.
 *
 * @remarks
 * - For non-hourly events, the function applies a multi-day end date adjustment if specified.
 * - Events with zero rounded duration and valid start/end dates are assigned a minimum duration of 1 (for non-hourly events).
 * - The function removes the processed events from the scheduler's event store and commits the project asynchronously.
 */
export const toSchedulerLineItemFromBryntumEvent = (
    events: EventModel[],
    project: ProjectModel,
    locale,
    options: ToSchedulerLineItemOptions = {}
): SchedulerLineItemFromBryntumEvent[] => {
    const { adjustMultiDayEnd = true, movingProduction = false } = options;

    const eventStore = project.eventStore;
    const lineItems: SchedulerLineItemFromBryntumEvent[] = [];
    const recordsToRemove: EventModel[] = [];
    for (const e of events) {
        const { id, startDate, endDate, durationUnit, duration, durationSpecified } = e;
        const isHourly = durationUnit === TIME_CONSTANTS.hour;
        const start = startDate ? toISOFromJSDate(startDate) : '';
        const startTime = isHourly && startDate ? toSimpleTimeFromJSDate(startDate) : '';
        // For non-hourly events, apply the multi-day + start-of-day adjustment.
        // For hourly events, or missing boundaries, just use endDate as-is.
        let adjustedEndDate: Date | null | undefined = endDate as Date | null | undefined;
        if (adjustMultiDayEnd && !isHourly && startDate && endDate) {
            adjustedEndDate = getAdjustedEndDateForMultiDay(startDate as Date, endDate as Date);
        }
        const end = adjustedEndDate ? toISOFromJSDate(adjustedEndDate) : '';
        const endTime = isHourly && endDate ? toSimpleTimeFromJSDate(endDate) : '';
        let roundedDuration = roundUpByDurationUnit(durationUnit, duration, locale);
        // If the rounded duration is 0 and the event has valid start and end dates,
        // set the duration to 1 for non-hourly events to ensure they are treated as having a minimum duration.
        if (roundedDuration === 0 && e.startDate && e.endDate && !isHourly) {
            roundedDuration = 1;
        }

        lineItems.push({
            id,
            dateRange: {
                start,
                end,
                startTime,
                endTime,
                duration: roundedDuration,
                dateTimeDuration: {
                    unit: durationUnit,
                    value: roundedDuration
                },
                durationSpecified
            }
        });
        const record = eventStore.getById(id);
        if (!record) continue;
        if (movingProduction) {
            if (id === CONSTANTS_ID.newEntityId) {
                recordsToRemove.push(record);
            }
        } else {
            recordsToRemove.push(record);
        }
    }
    if (recordsToRemove.length > 0) {
        eventStore.remove(recordsToRemove);
    }
    // Fire-and-forget is acceptable.
    project.commitAsync();
    return lineItems;
};

export const toBryntumDependenciesForActivity = (lineItems) => {
    const dependencies = [];
    lineItems.forEach((lineItem) => {
        if (lineItem.dependencies?.length) {
            lineItem.dependencies.forEach((dependency) => {
                const { fromActivity, toActivity, lag, lagUnit, type } = dependency;
                dependencies.push({
                    from: fromActivity,
                    to: toActivity,
                    lag: lag,
                    lagUnit: lagUnit,
                    type: type
                });
            });
        }
    });
    return { dependencies };
};

export const toBryntumDependencies = (schedulerLineItems: SchedulerLineItem[]) => {
    const dependencies = [];
    schedulerLineItems.forEach((sli, idx) => {
        if (idx < schedulerLineItems.length - 1) {
            dependencies.push({
                from: sli.id,
                to: schedulerLineItems[idx + 1].id,
                lag: 0,
                lagUnit: 'd',
                type: 2
            });
        }
    });
    return dependencies;
};

export const productToBryntumResourcesAndCalendars = (products) => {
    const resourceCalById = new Map<string, CalendarModel>();
    const resources = products?.map((r: Product) => {
        return {
            id: r.id,
            name: r.name,
            calendar: r.calendar ? r.calendar.id : undefined,
            type: r.type,
            subType: r.subType,
            locations: r.availableLocations,
            ownerOrganizationId: r.ownerOrganizationId,
            ownerOrganizationName: r.ownerOrganizationName,
            packageType: r.packagingType,
            offerType: r.offerTypes,
            category: r.categories?.map((c: any) => c.value),
            reference: r.reference
        };
    });

    return { resources, calendars: Array.from(resourceCalById.values()) };
};

export const planningResourceToBryntumResourcesAndCalendars = (planningResources: Resource[]) => {
    const resourceCalById = new Map<string, CalendarModel>();
    const resources = planningResources
        .filter((resource: Resource) => resource.production && !isEmpty(resource.production))
        .map((r: Resource) => {
            return {
                id: r.id,
                name: r.name,
                type: r.type,
                resourceCategory: r.resourceCategory,
                budgetCategory: r.budgetCategory,
                status: r.status,
                location: r.location,
                locationCity: r.location ? r.location.address?.locality : '',
                locationCountry: r.location ? r.location?.address?.countryRegion : '',
                production: r.production,
                'sr-type': SR_TYPES.Resource
            };
        });

    return { resources, calendars: Array.from(resourceCalById.values()) };
};

export const productionDataToBryntumFormat = (productionData) => {
    const production = [];
    const productionItem = {
        ...productionData,
        id: productionData.identity.id,
        'sr-type': SR_TYPES.Production
    };
    // deleting fields which are not required for bryntum store data
    delete productionItem.identity;
    delete productionItem.alternateIds;
    delete productionItem.associatedOrgs;
    delete productionItem.linkedPeople;
    delete productionItem.budget;
    delete productionItem.keyActivities;
    delete productionItem.keyPeople;
    delete productionItem.productionDates;
    production.push(productionItem);
    return { production };
};

export type SchedulerProduction = Production | ProductionServiceProduction;

export const toBryntumResourceProduction = (productionData: SchedulerProduction[]) => {
    const production = productionData.map((production: SchedulerProduction) => {
        return {
            ...production,
            id:
                ('id' in production ? production.id : undefined) ||
                ('identity' in production ? production.identity?.id : undefined) ||
                production?.reference?.id,
            'sr-type': SR_TYPES.Production
        };
    });
    return { production };
};

export const toBryntumResourcesAndCalendars = (schedulerLineItems: SchedulerLineItem[]) => {
    const resourceIds = new Set<string>();
    const resourceNames = new Map<string, String>();
    const resourceCalById = new Map<string, CalendarModel>();
    schedulerLineItems.forEach((sli, idx) => {
        sli.assignedResources.forEach((ar) => {
            resourceNames[ar.id] = ar.name;
        });
        sli.resources.forEach((resId) => {
            if (sli.dateRange.end != '') {
                resourceIds.add(resId);
            }
        });
    });
    const resources = Array.from(resourceIds).map((resId) => {
        return {
            id: resId,
            name: resourceNames[resId],
            calendar: `${resId}-cal`
        };
    });
    return { resources, calendars: Array.from(resourceCalById.values()) };
};

export const toLineItemsFromProductionActivity = (activities: Activity[], application: string) => {
    const shouldFilterByProduction = [PLANNING.name, PRODUCTION_PROFESSIONAL.name].includes(application);
    return activities
        .filter(
            (activity: Activity) => activity.dateRange && (!shouldFilterByProduction || !isEmpty(activity.production))
        )
        .map(toSchedulerActivityItemsFromProduction);
};

export const bookingInputToBryntumEvent = (bookingInput) => {
    const attributes = getEventAttributes(bookingInput);
    attributes.status = bookingInput.status;
    attributes.primary = bookingInput.primary;
    attributes.isChild = !bookingInput.hasDetailedSchedule;
    attributes.reservationGuestsAvailable = bookingInput.reservationGuestsAvailable;
    attributes.ownerOrganizationName = bookingInput.ownerOrganizationName;
    let assignedResources = [];
    assignedResources = bookingInput.associatedResources?.map((r) => {
        return { id: r.reference.id, name: r.label };
    });
    attributes.resourceLabel = bookingInput.status;
    attributes.opportunityName = bookingInput.name;
    attributes.opportunityType = bookingInput.OpportunityType;
    attributes.production = bookingInput.production?.name;
    attributes.organizationName = bookingInput.organization?.name;
    if (bookingInput?.contacts?.length) {
        const c = bookingInput.contacts[0];
        if (c.role) {
            attributes.contact = `${c.name.firstName} ${c.name.lastName} - ${c.role}`;
        } else {
            attributes.contact = `${c.name.firstName} ${c.name.lastName}`;
        }
    }
    if (bookingInput.organization && bookingInput.organization.name !== '') {
        attributes.organization = bookingInput.organization.name;
    }
    if (bookingInput.identity.id) {
        attributes.opportunityId = bookingInput.identity.id;
    } else if (bookingInput?.context?.id) {
        attributes.opportunityId = bookingInput.context.id;
    }
    return {
        ...bookingInput,
        id: bookingInput.id || bookingInput.identity.id,
        dateRange: bookingInput.dateRange || bookingInput.lineItems[0].dateRange,
        resources: bookingInput.associatedResources?.map((ar) => ar.reference.id),
        attributes,
        assignedResources,
        calendar: bookingInput.calendar ? bookingInput.calendar.id : undefined,
        organization: bookingInput.organization?.name,
        production: bookingInput.production?.name,
        opportunityName: bookingInput.name
    };
};

export const toBryntumCalendars = (calendarData) => {
    const bryntumCalendars = calendarData.map((calendar) => {
        return toBryntumCalendar(calendar);
    });
    return bryntumCalendars;
};

export const toBryntumCalendar = (calendar) => {
    const calendarsIntervals = toBryntumCalendarIntervals(calendar);
    return {
        id: calendar.id,
        name: calendar.name,
        unspecifiedTimeIsWorking: calendar.unspecifiedTimeIsWorking,
        intervals: calendarsIntervals,
        public: calendar.public
    };
};

export const getProjectCalendar = (calendars, organizationAccountDetails) => {
    // returns accountCalendar or system calendar if account cal is not assigned
    const accountCalendarId =
        organizationAccountDetails && !isNil(organizationAccountDetails.calendar)
            ? organizationAccountDetails.calendar.id
            : '';

    let projectCalendar = calendars.find((cal) => cal.id == accountCalendarId);
    if (!projectCalendar) {
        projectCalendar = calendars.find((cal) => cal.name == SYSTEM_CALENDAR);
    }
    return projectCalendar;
};

export const getCalendarAndEntity = (entityName, activeOrganizationAccount) => {
    const calendarsPromise = getCalendars(activeOrganizationAccount).then((res) => res?.data?.results?.hits?.items);
    const entityPromise = searchClient
        .query({
            query: slimResultsQuery(entityName),
            variables: {
                filters: [{ identifier: 'entity', value: entityName }],
                page: {
                    from: 0,
                    size: 2000
                }
            },
            fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
            context: {
                headers: {
                    ownerId: activeOrganizationAccount
                }
            }
        })
        .then((res) => res?.data?.results?.hits?.items);
    return { calendarsPromise, entityPromise };
};

export const getEventTooltipTemplateForActivity = (data, errorTitle = undefined, errorDescription = undefined) => {
    const firstLine = data.eventRecord.name;
    var secondLine = data.startText + ' - ' + data.endText;
    if (data.eventRecord.durationUnit) {
        const durationUnit = DURATION_UNIT_MAPPING.get(data.eventRecord.durationUnit);
        secondLine = secondLine + '(' + data.eventRecord.duration + durationUnit + ')';
    }
    const thirdLine = data.eventRecord.activityType || data.eventRecord['data'].activityType;
    var tooltipDisplay = `<div>${firstLine}</div>
                                <div>${secondLine}</div>
                                <div>${thirdLine}</div>`;
    if (!isEmpty(data.eventRecord.category) && !isEmpty(data.eventRecord.location)) {
        tooltipDisplay =
            tooltipDisplay +
            `<div>${data.eventRecord.category.value}</div>
                           <div>${data.eventRecord.location.label}</div>`;
    }
    if (data.eventRecord.data.hasError) {
        tooltipDisplay = tooltipDisplay + `<div>${errorTitle}. ${errorDescription}</div>`;
    }
    return tooltipDisplay;
};

export const editBryntumActivity = (schedulerInstance, event) => {
    schedulerInstance.eventStore.applyChangeset({
        updated: [
            { id: event.id, activityType: event.activityType },
            { id: event.id, dateRange: event.dateRange },
            { id: event.id, prouction: event.production },
            { id: event.id, productReferences: event.productReferences }
        ]
    });
};

export const getEventTooltipTemplateForReservation = (data) => {
    const event = data.eventRecord;
    const attributes = event.attributes || event.data.attributes;

    return event.isMilestone
        ? `<div><b>${data.eventRecord.attributes.opportunityName}</b></div>
                            <div>${DateHelper.format(data.eventRecord.startDate, 'MMM DD, YYYY')}</div>`
        : attributes.showTime
          ? `<div style="display: flex; flex-direction: column;">
                <div><b>${attributes.opportunityName}</b></div>
                <div>${attributes.status}</div>
                <div>${DateHelper.format(event.startDate, 'MMM DD, YYYY hh:mm a')} -
                     ${DateHelper.format(event.endDate, 'MMM DD, YYYY hh:mm a')}</div>
                <div>${attributes.contact}</div>
                <div>${attributes.organization}</div>
                <div>${attributes.production}</div>
           </div>`
          : `<div style="display: flex; flex-direction: column;">
                <div><b>${attributes.opportunityName}</b></div>
                <div>${attributes.status}</div>
                <div>${data.startText} - ${data.endText}</div>
                <div>${attributes.contact}</div>
                <div>${attributes.organization}</div>
                <div>${attributes.production}</div>
           </div>`;
};

export const createBryntumDependenciesForBookingLineItems = (lineItems) => {
    const dependencies = [];
    const groupedChildren = {};

    lineItems.forEach((li) => {
        if (li.attributes.groupType === lineItemGroupTypes.child && li.attributes.parent?.reference?.id) {
            const parentId = li.attributes.parent.reference.id;
            if (!groupedChildren[parentId]) groupedChildren[parentId] = [];
            groupedChildren[parentId].push(li);
        }
    });

    Object.values(groupedChildren).forEach((children) => {
        for (let i = 0; i < children.length - 1; i++) {
            const from = children[i];
            const to = children[i + 1];

            dependencies.push({
                from: from.id,
                to: to.id,
                type: 2,
                lag: 0,
                lagUnit: 'd'
            });
        }
    });

    return dependencies;
};

export const getUniqueAssignments = (
    assignments: any[],
    filterCondition: (a: any) => boolean,
    uniqueKeyFn: (a: any) => string | number
) => {
    return uniqBy(assignments.filter(filterCondition), uniqueKeyFn);
};

export const unitMapping = {
    next12Months: TIME_UNITS.day,
    year: TIME_UNITS.day,
    next30Days: TIME_UNITS.day,
    month: TIME_UNITS.day,
    weekCustom: TIME_UNITS.day,
    dayCustom: TIME_UNITS.hour,
    default: TIME_UNITS.day
};

export const daysOfTheWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const extractTiming = (activity: MoveProductionScheduleActivity): MoveProductionScheduleActivityTimingMeta => {
    const activityId = activity?.identity?.id ?? activity?.id;
    const calendar = activity?.calendar;
    const dr = activity?.dateRange;
    const dd = dr?.dateTimeDuration;

    const { mergedStart, mergedEnd } = getMergedStartEnd(dr);
    return {
        activityId,
        calendar,
        isHourly: dd?.unit === TIME_CONSTANTS.hour,
        durationUnit: DURATION_UNIT_MAPPING.get(dd?.unit),
        duration: dd?.value,
        durationSpecified: !!dr?.durationSpecified,
        mergedStart,
        mergedEnd,
        startValid: isValid(mergedStart),
        endValid: isValid(mergedEnd)
    };
};

/**
 * Normalizes a DateRange before sending it to the scheduler.
 *
 * - Returns the input as-is when the range is nullish or when the dates are valid and ordered.
 * - If both `start` and `end` are valid ISO dates and `start > end`, we **clamp** the range to a
 *   zero-length interval on the `start` date by setting `end = start`.
 *   This prevents negative or inverted ranges, which the scheduler cannot handle reliably.
 *
 * Note: We also ensure `startTime` and `endTime` are non-null by defaulting:
 * - `startTime` → `START_OF_DAY` when missing
 * - `endTime`   → `endTime` if provided, otherwise `startTime`
 */
export const normalizeDateRangeForScheduler = (
    dateRange: DateRange | null | undefined
): DateRange | null | undefined => {
    if (!dateRange) return dateRange;

    const { start, end } = dateRange;
    if (start && end && isValid(parseISO(start)) && isValid(parseISO(end)) && parseISO(start) > parseISO(end)) {
        const safeStartTime = dateRange.startTime ?? START_OF_DAY;
        const safeEndTime = dateRange.endTime ?? safeStartTime;
        return {
            ...dateRange,
            end: start, //Clamp to a zero-length range by setting end = start
            startTime: safeStartTime,
            endTime: safeEndTime
        };
    }
    return dateRange;
};

const getMergedRangeInfo = (range: DateRange) => {
    const { mergedStart, mergedEnd } = getMergedStartEnd(range);
    const startValid = isValid(mergedStart);
    const endValid = isValid(mergedEnd);

    return { mergedStart, mergedEnd, startValid, endValid };
};

/**
 * Adjusts and validates the provided date range and start/end dates for auto-scheduling.
 *
 * - For multi-day spans (not in hourly mode), snaps the end time to the start of the day.
 * - For same-day ranges, preserves the original times to maintain partial-day durations.
 * - In hourly mode, mirrors the provided start and end dates.
 *
 * @param dateRange - The original date range to be normalized and validated.
 * @param startDate - The initial start date as an ISO string or undefined.
 * @param endDate - The initial end date as an ISO string or undefined.
 * @returns An object containing the normalized date range and potentially updated start and end dates.
 */
const checkDateBeforeAutoScheduling = (
    dateRange: DateRange,
    startDate: string | undefined,
    endDate: string | undefined,
    options: { treatEndAsInclusive?: boolean } = {}
): { dateRange: DateRange; startDate: string | undefined; endDate: string | undefined } => {
    const { treatEndAsInclusive = false } = options;
    if (!dateRange) {
        return { dateRange, startDate, endDate };
    }

    let normalizedDateRange = normalizeDateRangeForScheduler(dateRange);

    // If normalization decides to null out the range, propagate it.
    if (!normalizedDateRange) {
        return { dateRange: normalizedDateRange, startDate, endDate };
    }

    const isHourly = normalizedDateRange?.dateTimeDuration?.unit === TIME_CONSTANTS.hour;

    let { mergedStart, mergedEnd, startValid, endValid } = getMergedRangeInfo(normalizedDateRange);

    const computeEndDate = (end: Date, fallback?: string) => {
        if (!endValid) return fallback ?? undefined;

        // Inclusive end day -> exclusive end instant for Bryntum (day-based + durationSpecified=false)
        if (treatEndAsInclusive && !isHourly && !normalizedDateRange!.durationSpecified) {
            const exclusiveEnd = DateHelper.add(end, 1, TIME_CONSTANTS.d);
            return toDateTimeISOWithoutOffset(exclusiveEnd);
        }

        return toDateTimeISOWithoutOffset(end);
    };

    // For multi-day (non-hourly) spans, snap end to start-of-day
    if (!isHourly && startValid && endValid && !isSameDay(mergedStart, mergedEnd)) {
        normalizedDateRange = {
            ...normalizedDateRange,
            endTime: START_OF_DAY
        };

        const dateInfo = getMergedRangeInfo(normalizedDateRange);
        mergedStart = dateInfo.mergedStart;
        mergedEnd = dateInfo.mergedEnd;
        startValid = dateInfo.startValid;
        endValid = dateInfo.endValid;
    }

    startDate = startValid ? toDateTimeISOWithoutOffset(mergedStart) : (startDate ?? undefined);
    endDate = computeEndDate(mergedEnd, endDate);

    return { dateRange: normalizedDateRange, startDate, endDate };
};

/**
 * Updates a list of scheduler line items for use with the auto-scheduler.
 *
 * This function normalizes event objects by adjusting their date ranges and calendar references,
 * and removes or modifies certain properties based on whether a duration is specified.
 * It also ensures that all returned events are marked as not manually scheduled.
 *
 * @param lineItems - The array of scheduler line items to be processed.
 * @param sequence - A boolean indicating whether to sequence the events.
 * @returns An object containing the updated events and their corresponding assignments.
 */
export const updateEventsForAutoScheduler = (
    lineItems: SchedulerLineItem[],
    sequence: boolean,
    options: { treatEndAsInclusive?: boolean } = {}
) => {
    const { events, assignments } = toBryntumEventsAndAssignments(lineItems, sequence);
    const updatedEvents = events.map((event) => {
        const { dateRange, calendar, ...rest } = event;
        const durationSpecified = !!dateRange?.durationSpecified;
        const normalizedCalendar =
            typeof calendar === 'string'
                ? calendar
                : calendar && typeof calendar.id === 'string'
                  ? calendar.id
                  : calendar;

        let normalizedDateRange = normalizeDateRangeForScheduler(dateRange);
        let startDate = event.startDate;
        let endDate = event.endDate;
        if (dateRange) {
            const result = checkDateBeforeAutoScheduling({ ...dateRange }, startDate, endDate, options);
            normalizedDateRange = result.dateRange;
            startDate = result.startDate ?? startDate;
            endDate = result.endDate ?? endDate;
        }
        if (normalizedDateRange) {
            if (durationSpecified) {
                // remove dateRange.end in duration mode
                const { end, ...restDateRange } = normalizedDateRange;
                normalizedDateRange = { ...restDateRange };
            } else {
                // remove value for durationSpecified=false
                const { dateTimeDuration, ...restDateRange } = normalizedDateRange;
                const normalizedDateTimeDuration = dateTimeDuration ? { unit: dateTimeDuration.unit } : undefined;

                normalizedDateRange = {
                    ...restDateRange,
                    ...(normalizedDateTimeDuration && {
                        dateTimeDuration: normalizedDateTimeDuration
                    })
                };
            }
        }
        let baseEventProps: any;
        if (durationSpecified) {
            // remove event.endDate
            const { endDate: _ignoredEndDate, ...restWithoutEndDate } = rest;
            baseEventProps = {
                ...restWithoutEndDate,
                startDate
            };
        } else {
            // remove event.duration
            const { duration: _ignoredDuration, ...restWithoutDuration } = rest;
            baseEventProps = {
                ...restWithoutDuration,
                startDate,
                endDate
            };
        }
        return {
            ...baseEventProps,
            dateRange: normalizedDateRange,
            calendar: normalizedCalendar,
            manuallyScheduled: false,
            ignoreResourceCalendar: true
        };
    });

    return { events: updatedEvents, assignments };
};

export const toValidDate = (value): Date | undefined => {
    if (!value) return undefined;

    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

export const getHoursSince = (date: Date | undefined): number | undefined => {
    if (!date) return undefined;
    return DateHelper.as('hour', Date.now() - date.getTime(), 'ms');
};

/**
 * Validates a single line item's date range and duration for scheduling.
 *
 * @param lineItem - The line item to validate
 * @param t - Translation function from useI18n()
 * @returns An object with `isValid` boolean and optional `error` message
 */
const validateSchedulerInputLineItem = (
    lineItem: any,
    t: (key: string) => string
): { isValid: boolean; error?: string } => {
    const { dateRange } = lineItem;

    if (!dateRange) {
        return { isValid: false, error: t('autoSchedule.error.missingDateRange') };
    }

    const { start, end, durationSpecified, dateTimeDuration } = dateRange;

    if (!start) {
        return { isValid: false, error: t('autoSchedule.error.missingStartDate') };
    }

    if (durationSpecified) {
        // Duration mode: must have non-zero duration
        if (!dateTimeDuration?.value || dateTimeDuration.value <= 0) {
            return { isValid: false, error: t('autoSchedule.error.durationMustBePositive') };
        }
    } else {
        // End date mode: must have end date >= start date
        if (!end) {
            return { isValid: false, error: t('autoSchedule.error.missingEndDate') };
        }

        const startDate = parseISO(start);
        const endDate = parseISO(end);

        if (!isValid(startDate) || !isValid(endDate)) {
            return { isValid: false, error: t('autoSchedule.error.invalidDateFormat') };
        }

        if (endDate < startDate) {
            return { isValid: false, error: t('autoSchedule.error.endDateBeforeStartDate') };
        }
    }

    return { isValid: true };
};

/**
 * Validates schedule line items before calling the auto-scheduler.
 *
 * Validation criteria:
 * 1. Stand-alone items: Must have valid start date and either non-zero duration (duration mode) or valid end date >= start date (end date mode)
 * 2. Group items (non-detailed): Same as stand-alone items
 * 3. Group items (detailed, non-sequential): All children must individually pass validation
 * 4. Group items (detailed, sequential): First child must pass full validation, remaining children must have duration/end date
 *
 * @param schedulerInput - Array of line items to be scheduled
 * @param selectedLineItem - The currently selected line item
 * @param t - Translation function from useI18n()
 * @returns An object with `isValid` boolean and optional `error` message
 */
export const validateSchedulerInput = (
    schedulerInput: any[],
    selectedLineItem: any,
    t: (key: string) => string
): { isValid: boolean; error?: string } => {
    if (!schedulerInput || schedulerInput.length === 0) {
        return { isValid: false, error: t('autoSchedule.error.noLineItems') };
    }

    const { groupType, needsDetailedSchedule, shouldSequenceSchedule } = selectedLineItem;
    const isSequential = shouldSequenceSchedule || false;

    // Case 1: Stand-alone line item (not a group)
    if (groupType !== lineItemGroupTypes.group) {
        return validateSchedulerInputLineItem(selectedLineItem, t);
    }

    // Case 2: Group type, non-detailed calculation (at group level)
    if (!needsDetailedSchedule) {
        return validateSchedulerInputLineItem(selectedLineItem, t);
    }

    // Case 3 & 4: Group type with detailed calculation (at child level)
    const childItems = schedulerInput.filter((li) => li.groupType === lineItemGroupTypes.child);

    if (childItems.length === 0) {
        return { isValid: false, error: t('autoSchedule.error.noChildItems') };
    }

    if (isSequential) {
        // Case 4: Sequential mode - validate first child fully, others need duration/end date
        const firstChild = childItems[0];
        const firstChildValidation = validateSchedulerInputLineItem(firstChild, t);

        if (!firstChildValidation.isValid) {
            return {
                isValid: false,
                error: `${t('autoSchedule.error.firstChildPrefix')}: ${firstChildValidation.error}`
            };
        }

        // Validate remaining children have duration or end date
        for (let i = 1; i < childItems.length; i++) {
            const child = childItems[i];
            const { dateRange } = child;

            if (!dateRange) {
                return {
                    isValid: false,
                    error: `${t('autoSchedule.error.childItemPrefix')} ${i + 1}: ${t('autoSchedule.error.missingDateRange')}`
                };
            }

            if (dateRange.durationSpecified) {
                if (!dateRange.dateTimeDuration?.value || dateRange.dateTimeDuration.value <= 0) {
                    return {
                        isValid: false,
                        error: `${t('autoSchedule.error.childItemPrefix')} ${i + 1}: ${t('autoSchedule.error.durationMustBePositive')}`
                    };
                }
            } else {
                if (!dateRange.end) {
                    return {
                        isValid: false,
                        error: `${t('autoSchedule.error.childItemPrefix')} ${i + 1}: ${t('autoSchedule.error.missingEndDate')}`
                    };
                }
            }
        }
    } else {
        // Case 3: Non-sequential mode - validate each child individually
        for (let i = 0; i < childItems.length; i++) {
            const childValidation = validateSchedulerInputLineItem(childItems[i], t);
            if (!childValidation.isValid) {
                return {
                    isValid: false,
                    error: `${t('autoSchedule.error.childItemPrefix')} ${i + 1}: ${childValidation.error}`
                };
            }
        }
    }

    return { isValid: true };
};

export const hasAssignmentIdentifier = (value) => value !== undefined && value !== null && value !== '';

export const hasAssignmentEventResourceKey = (assignment) =>
    hasAssignmentIdentifier(assignment?.eventId) && hasAssignmentIdentifier(assignment?.resourceId);

export const hasAssignmentLineItemKey = (assignment) => hasAssignmentIdentifier(assignment?.lineItemId);

export const isAssignmentWithoutLineItemKey = (assignment) => !hasAssignmentLineItemKey(assignment);

/**
 * Generates a unique key for a planning assignment based on its identifiers.
 *
 * The key is constructed as follows:
 * - If both `eventId` and `resourceId` are present and valid, returns a key in the format `event:{eventId}:{resourceId}`.
 * - If only `id` is present and valid, returns a key in the format `id:{id}`.
 * - If none of the identifiers are valid, returns `undefined`.
 *
 * @param assignment - The assignment object containing possible identifiers (`eventId`, `resourceId`, or `id`).
 * @returns A unique string key for the assignment, or `undefined` if no valid identifiers are found.
 */
export const getPlanningAssignmentKey = (assignment) => {
    if (hasAssignmentEventResourceKey(assignment)) {
        return `event:${assignment.eventId}:${assignment.resourceId}`;
    }
    if (hasAssignmentIdentifier(assignment?.id)) {
        return `id:${assignment.id}`;
    }
    return undefined;
};

/**
 * Generates a unique key string for a planning dependency object.
 *
 * The key is constructed using the `from`, `to`, `type`, `lag`, and `lagUnit` properties of the dependency.
 * If `from` or `to` is an object, their `id` property is used; otherwise, the value itself is used.
 * If either `from` or `to` is missing or falsy, the function returns `undefined`.
 *
 * @param dependency - The dependency object containing `from`, `to`, `type`, `lag`, and `lagUnit` properties.
 * @returns A string key in the format `${from}:${to}:${type}:${lag}:${lagUnit}`, or `undefined` if `from` or `to` is missing.
 */
export const getPlanningDependencyKey = (dependency) => {
    const from = typeof dependency?.from === 'object' ? dependency?.from?.id : dependency?.from;
    const to = typeof dependency?.to === 'object' ? dependency?.to?.id : dependency?.to;

    if (!from || !to) {
        return undefined;
    }

    return `${from}:${to}:${dependency?.type ?? ''}:${dependency?.lag ?? ''}:${dependency?.lagUnit ?? ''}`;
};

/**
 * Computes the delta between the current and next planning store data.
 *
 * This function compares the `events`, `resources`, `assignments`, and `dependencies` arrays
 * in the `currentStoreData` and `nextStoreData` objects. It returns only the new items from
 * `nextStoreData` that do not exist in `currentStoreData`, based on their unique identifiers.
 * The `calendars` array is always returned as empty, and `sync_meta` is taken from `nextStoreData`.
 *
 * @param currentStoreData - The current state of the planning store, containing arrays of events, resources, assignments, and dependencies.
 * @param nextStoreData - The next state of the planning store to compare against, containing arrays of events, resources, assignments, and dependencies.
 * @returns An object containing arrays of new `events`, `resources`, `assignments`, and `dependencies` found in `nextStoreData` but not in `currentStoreData`, an empty `calendars` array, and the `sync_meta` from `nextStoreData`.
 */
export const getPlanningStoreDelta = (currentStoreData, nextStoreData) => {
    const existingEventIds = new Set((currentStoreData?.events || []).map((event) => event?.id).filter(Boolean));
    const existingResourceIds = new Set(
        (currentStoreData?.resources || []).map((resource) => resource?.id).filter(Boolean)
    );
    const existingAssignmentKeys = new Set(
        (currentStoreData?.assignments || []).map(getPlanningAssignmentKey).filter(Boolean)
    );
    const existingDependencyKeys = new Set(
        (currentStoreData?.dependencies || []).map(getPlanningDependencyKey).filter(Boolean)
    );

    return {
        events: (nextStoreData?.events || []).filter((event) => event?.id && !existingEventIds.has(event.id)),
        resources: (nextStoreData?.resources || []).filter(
            (resource) => resource?.id && !existingResourceIds.has(resource.id)
        ),
        assignments: (nextStoreData?.assignments || []).filter((assignment) => {
            const key = getPlanningAssignmentKey(assignment);
            return key && !existingAssignmentKeys.has(key);
        }),
        dependencies: (nextStoreData?.dependencies || []).filter((dependency) => {
            const key = getPlanningDependencyKey(dependency);
            return key && !existingDependencyKeys.has(key);
        }),
        calendars: [],
        sync_meta: nextStoreData?.sync_meta || {}
    };
};

export const createSchedulerStore = (calendars = [], overrides = {}) => ({
    events: [],
    resources: [],
    assignments: [],
    calendars,
    lastFetchedAt: undefined,
    eventsFetchedAt: undefined,
    ...overrides
});

/**
 * Generates a unique key for an assignment object based on its identifiers.
 *
 * The key is determined in the following order of precedence:
 * 1. If the assignment has both a valid `eventId` and `resourceId`, returns `event:<eventId>:<resourceId>`.
 * 2. If the assignment has a valid `lineItemId`, returns `lineItem:<lineItemId>`.
 * 3. If the assignment has a valid `id`, returns `id:<id>`.
 * 4. If none of the above identifiers are present, returns `undefined`.
 *
 * @param assignment - The assignment object to generate a key for.
 * @returns A unique string key for the assignment, or `undefined` if no valid identifiers are found.
 */
export const getAssignmentKey = (assignment) => {
    if (hasAssignmentEventResourceKey(assignment)) {
        return `event:${assignment.eventId}:${assignment.resourceId}`;
    }
    if (hasAssignmentLineItemKey(assignment)) {
        return `lineItem:${assignment.lineItemId}`;
    }
    if (hasAssignmentIdentifier(assignment?.id)) {
        return `id:${assignment.id}`;
    }
    return undefined;
};

export const getReservationAssignmentsFromEvents = (
    events: ReservationAssignmentEvent[] | null | undefined = []
): ReservationAssignmentFromEvent[] => {
    const safeEvents = Array.isArray(events) ? events : [];

    return safeEvents.flatMap((event) => {
        const eventId = event?.id;
        const lineItemId = hasAssignmentIdentifier(event?.lineItemId)
            ? event.lineItemId
            : event?.attributes?.lineItemId;
        const resources = Array.isArray(event?.resources) ? event.resources : [];

        if (!hasAssignmentIdentifier(eventId) || !resources.length) {
            return [];
        }

        return resources
            .map(getReservationAssignmentResourceId)
            .filter(hasAssignmentIdentifier)
            .map((resourceId) => ({
                resourceId,
                eventId,
                lineItemId
            }));
    });
};

/**
 * Merges two arrays of assignment objects, removing duplicates based on a unique assignment key.
 * If an assignment with the same key exists in both arrays, the one from `nextAssignments` will overwrite the one from `currentAssignments`.
 *
 * @param currentAssignments - The initial array of assignment objects.
 * @param nextAssignments - The array of assignment objects to merge into the initial array.
 * @returns A new array containing merged assignments with unique keys.
 */
export const mergeAssignments = (currentAssignments = [], nextAssignments = []) => {
    const assignmentMap = new Map<string, any>();

    currentAssignments.forEach((assignment) => {
        const key = getAssignmentKey(assignment);
        if (key) {
            assignmentMap.set(key, assignment);
        }
    });

    nextAssignments.forEach((assignment) => {
        const key = getAssignmentKey(assignment);
        if (key) {
            assignmentMap.set(key, assignment);
        }
    });

    return [...assignmentMap.values()];
};

/**
 * Computes the delta between the current and next scheduler store data.
 *
 * Returns the new events, resources, and assignments present in `nextStoreData` that do not exist in `currentStoreData`.
 * The function compares items by their `id` (for events and resources) or a composite key (for assignments).
 * The `calendars` field is always returned as an empty array.
 *
 * @param currentStoreData - The current state of the scheduler store, containing arrays of events, resources, and assignments.
 * @param nextStoreData - The next state of the scheduler store to compare against the current state.
 * @returns An object containing arrays of new `events`, `resources`, and `assignments` found in `nextStoreData` but not in `currentStoreData`, and an empty `calendars` array.
 */
export const getSchedulerStoreDelta = (currentStoreData, nextStoreData) => {
    const existingEventIds = new Set((currentStoreData?.events || []).map((event) => event?.id).filter(Boolean));
    const existingResourceIds = new Set(
        (currentStoreData?.resources || []).map((resource) => resource?.id).filter(Boolean)
    );
    const existingAssignmentKeys = new Set((currentStoreData?.assignments || []).map(getAssignmentKey).filter(Boolean));

    return {
        events: (nextStoreData?.events || []).filter((event) => event?.id && !existingEventIds.has(event.id)),
        resources: (nextStoreData?.resources || []).filter(
            (resource) => resource?.id && !existingResourceIds.has(resource.id)
        ),
        assignments: (nextStoreData?.assignments || []).filter((assignment) => {
            const key = getAssignmentKey(assignment);
            return key && !existingAssignmentKeys.has(key);
        }),
        calendars: []
    };
};

/**
 * Determines whether a given resource matches all key-value pairs specified in the resource filter data.
 *
 * @param resource - The resource object to be checked against the filter criteria.
 * @param resourceFilterData - An object containing key-value pairs to filter the resource by. Defaults to an empty object.
 * @returns `true` if the resource matches all filter criteria or if the filter data is empty; otherwise, `false`.
 */
export const matchesResourceFilterData = (
    resource: Record<string, string | number> | null | undefined,
    resourceFilterData: Record<string, string | number | boolean> = {}
): boolean => {
    const entries = Object.entries(resourceFilterData);

    if (entries.length === 0) {
        return true;
    }

    if (!resource) {
        return false;
    }

    return entries.every(([key, value]) => resource[key] === value);
};
